/**
 * Theme management utility for dark mode toggle
 * Supports three modes: 'light', 'dark', and 'auto' (system preference)
 */

import type { Theme, EffectiveTheme } from '../types/app.d.ts';

const THEME_KEY = 'murphys_theme';
const VALID_THEMES: Theme[] = ['light', 'dark', 'auto'];

/**
 * Get the current theme preference from localStorage
 * @returns {'light' | 'dark' | 'auto'} The stored theme or 'auto' if not set
 */
export function getTheme(): Theme {
  if (typeof localStorage === 'undefined') {
    return 'auto';
  }
  try {
    const stored = localStorage.getItem(THEME_KEY);
    return VALID_THEMES.includes(stored as Theme) ? stored as Theme : 'auto';
  } catch {
    // localStorage may be unavailable (private mode, etc.)
    return 'auto';
  }
}

/**
 * Save theme preference to localStorage and apply to DOM
 * @param {'light' | 'dark' | 'auto'} theme - The theme to set
 */
export function setTheme(theme: Theme): void {
  if (!VALID_THEMES.includes(theme)) {
    return;
  }
  
  if (typeof localStorage !== 'undefined') {
    try {
      localStorage.setItem(THEME_KEY, theme);
    } catch {
      // localStorage may be unavailable (private mode, quota exceeded, etc.)
    }
  }
  
  applyTheme(theme);
}

/**
 * Get the effective theme (resolves 'auto' to actual light/dark based on system preference)
 * @param {'light' | 'dark' | 'auto'} theme - The theme preference
 * @returns {'light' | 'dark'} The resolved theme
 */
export function getEffectiveTheme(theme: Theme): EffectiveTheme {
  if (theme === 'auto') {
    if (typeof window !== 'undefined' && window.matchMedia) {
      return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    }
    return 'light';
  }
  return theme;
}

/**
 * Apply theme to the DOM by setting data-theme attribute on html element
 * @param {'light' | 'dark' | 'auto'} [theme] - Theme to apply (defaults to stored theme)
 */
export function applyTheme(theme?: Theme): void {
  if (typeof document === 'undefined') {
    return;
  }
  
  const themeToApply = theme ?? getTheme();
  
  if (themeToApply === 'auto') {
    // Remove data-theme to let CSS media query handle it
    document.documentElement.removeAttribute('data-theme');
  } else {
    document.documentElement.setAttribute('data-theme', themeToApply);
  }
  
  // Dispatch custom event for components that need to react to theme changes
  document.dispatchEvent(new CustomEvent('themechange', { 
    detail: { 
      theme: themeToApply,
      effectiveTheme: getEffectiveTheme(themeToApply)
    } 
  }));
}

/**
 * Cycle through themes: auto -> light -> dark -> auto
 * @returns {'light' | 'dark' | 'auto'} The new theme after cycling
 */
export function cycleTheme(): Theme {
  const current = getTheme();
  const order: Theme[] = ['auto', 'light', 'dark'];
  const currentIndex = order.indexOf(current);
  // order has 3 elements, index always valid
  const nextTheme = order[(currentIndex + 1) % order.length]!;
  setTheme(nextTheme);
  return nextTheme;
}

/**
 * Get the icon name to display based on current theme
 * @param {'light' | 'dark' | 'auto'} [theme] - Theme to get icon for (defaults to stored theme)
 * @returns {'sun' | 'moon' | 'sunMoon'} Icon name for the theme toggle button
 */
export function getThemeIcon(theme?: Theme): string {
  const currentTheme = theme ?? getTheme();
  switch (currentTheme) {
    case 'light':
      return 'sun';
    case 'dark':
      return 'moon';
    case 'auto':
    default:
      return 'sunMoon';
  }
}

/**
 * Get accessible label for the theme toggle button
 * @param {'light' | 'dark' | 'auto'} [theme] - Current theme (defaults to stored theme)
 * @returns {string} Accessible label describing current state and action
 */
export function getThemeLabel(theme?: Theme): string {
  const currentTheme = theme ?? getTheme();
  switch (currentTheme) {
    case 'light':
      return 'Theme: Light. Click for dark mode';
    case 'dark':
      return 'Theme: Dark. Click for system preference';
    case 'auto':
    default:
      return 'Theme: Auto. Click for light mode';
  }
}

/**
 * Get tooltip text for the theme toggle button
 * @param {'light' | 'dark' | 'auto'} [theme] - Current theme (defaults to stored theme)
 * @returns {string} Short tooltip text describing current theme mode
 */
export function getThemeTooltip(theme?: Theme): string {
  const currentTheme = theme ?? getTheme();
  switch (currentTheme) {
    case 'light':
      return 'Light mode';
    case 'dark':
      return 'Dark mode';
    case 'auto':
    default:
      return 'Auto mode';
  }
}

/**
 * Initialize theme system - apply stored theme and set up system preference listener
 */
export function initTheme(): void {
  // Apply stored theme immediately
  applyTheme();
  
  // Listen for system preference changes (only matters when in 'auto' mode)
  if (typeof window !== 'undefined' && window.matchMedia) {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = () => {
      if (getTheme() === 'auto') {
        document.dispatchEvent(new CustomEvent('themechange', {
          detail: { theme: 'auto', effectiveTheme: mediaQuery.matches ? 'dark' : 'light' }
        }));
      }
    };
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleChange);
    }
  }
}
